const faDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

/** Renders Western digits as Persian digits for `fa` locale number display. */
export function toPersianDigits(value: string | number): string {
  return String(value).replace(/[0-9]/g, (digit) => faDigits[Number(digit)] ?? digit);
}

/**
 * `245` -> `۴:۰۵` (or `4:05` with `"en"`). Song/video duration is stored in whole seconds.
 * Durations of an hour or more render as `h:mm:ss`.
 */
export function formatDuration(totalSeconds: number, locale: "fa" | "en" = "fa"): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const ss = seconds.toString().padStart(2, "0");
  const formatted =
    hours > 0 ? `${hours}:${minutes.toString().padStart(2, "0")}:${ss}` : `${minutes}:${ss}`;
  return locale === "fa" ? toPersianDigits(formatted) : formatted;
}

export function formatNumber(value: number, locale: "fa" | "en" = "en"): string {
  const formatted = new Intl.NumberFormat("en-US").format(value);
  return locale === "fa" ? toPersianDigits(formatted) : formatted;
}

/** Persian (Solar Hijri / Jalali) calendar with Persian digits. */
const JALALI_LOCALE = "fa-IR-u-ca-persian-nu-arabext";

type DateInput = string | number | Date | null | undefined;

function toValidDate(value: DateInput): Date | null {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const jalaliFormatters = new Map<string, Intl.DateTimeFormat>();

function jalaliFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);
  let formatter = jalaliFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(JALALI_LOCALE, options);
    jalaliFormatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Human-readable Jalali date, e.g. `2026-10-16` -> `۲۴ مهر ۱۴۰۵`.
 * Missing/invalid values render as an em dash. Use only for display — keep
 * machine-readable values (JSON-LD, `<time dateTime>`, sitemap) in ISO.
 */
export function formatJalaliDate(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = { dateStyle: "long" },
): string {
  const date = toValidDate(value);
  return date ? jalaliFormatter(options).format(date) : "—";
}

/** Jalali year with Persian digits, e.g. `۱۴۰۵`. Defaults to the current year. */
export function jalaliYear(value: DateInput = new Date()): string {
  const date = toValidDate(value);
  return date ? jalaliFormatter({ year: "numeric" }).format(date) : "—";
}

/**
 * Both UIs are Persian, so dates default to the Jalali calendar (`fa-IR`, Persian digits)
 * with time. Missing/invalid values render as an em dash instead of "Invalid Date".
 */
export function formatDate(value: DateInput, locale: "fa" | "en" = "fa"): string {
  const date = toValidDate(value);
  if (!date) return "—";
  const options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };
  return locale === "fa"
    ? jalaliFormatter(options).format(date)
    : new Intl.DateTimeFormat("en-US", options).format(date);
}

/** `"۵ دقیقه پیش"` style relative time in Persian; falls back to a Jalali date after a week. */
export function formatTimeAgo(value: DateInput, now: number = Date.now()): string {
  const date = toValidDate(value);
  if (!date) return "—";
  const seconds = Math.max(0, Math.floor((now - date.getTime()) / 1000));
  if (seconds < 60) return "همین الان";
  if (seconds < 3600) return `${toPersianDigits(Math.floor(seconds / 60))} دقیقه پیش`;
  if (seconds < 86400) return `${toPersianDigits(Math.floor(seconds / 3600))} ساعت پیش`;
  if (seconds < 7 * 86400) return `${toPersianDigits(Math.floor(seconds / 86400))} روز پیش`;
  return formatJalaliDate(date);
}
